// =========================================
// HDR PHOTO TYPES
// =========================================

export type ListingStatus = 'pending' | 'processing' | 'ready' | 'error';
export type MediaItemType = 'stack' | 'single';
export type MediaItemStatus = 'pending' | 'uploading' | 'processing' | 'ready' | 'error';
export type ProcessingType = 'hdr_merge' | 'twilight' | 'auto_remove' | 'grass' | 'staging' | 'curves' | 'blue_sky' | 'verticals';
export type QueueStatus = 'queued' | 'processing' | 'completed' | 'failed';

/** Worker `metadata.pipeline` for initial HDR merge jobs (see HDR_PIPELINE on worker). */
export type HdrMergePipeline = 'autoenhance' | 'fal' | 'gemini' | 'autopix';

export const HDR_MERGE_PIPELINE_OPTIONS: {
  value: HdrMergePipeline;
  label: string;
  hint: string;
}[] = [
  { value: 'autoenhance', label: 'Autoenhance.ai', hint: 'Backup — fast cloud HDR + enhancement' },
  { value: 'autopix', label: 'Esoft Autopix', hint: 'eWarp Autopix — async, ~40 min' },
  { value: 'gemini', label: 'Rehub + Gemini', hint: 'Local merge + Gemini colour' },
  { value: 'fal', label: 'Rehub + fal.ai', hint: 'Local merge, fal sky / color / upscale' },
];

export const HDR_PRIMARY_PIPELINE_OPTIONS = HDR_MERGE_PIPELINE_OPTIONS.filter(
  (o) => o.value === 'autoenhance' || o.value === 'autopix'
);

export function parseHdrMergePipeline(raw: unknown): HdrMergePipeline | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const t = raw.trim().toLowerCase();
  if (t === 'autoenhance' || t === 'ae' || t === 'auto') return 'autoenhance';
  if (t === 'fal') return 'fal';
  if (t === 'gemini' || t === 'google' || t === 'gemini_pro') return 'gemini';
  if (t === 'autopix' || t === 'esoft' || t === 'ewarp' || t === 'esoft_autopix') return 'autopix';
  return null;
}

/** Last-resort seed from VITE_HDR_QUEUE_PIPELINE. Live default is hdr_app_settings. */
export function defaultHdrMergePipelineFromEnv(): HdrMergePipeline {
  return parseHdrMergePipeline(import.meta.env.VITE_HDR_QUEUE_PIPELINE) || 'autoenhance';
}

export interface Listing {
  id: string;
  user_id: string;
  address: string;
  name?: string;
  cover_image_url?: string;
  status: ListingStatus;
  media_count: number;
  created_at: string;
  updated_at: string;
}

export interface MediaItem {
  id: string;
  listing_id: string;
  type: MediaItemType;
  status: MediaItemStatus;
  sequence_number?: number;
  error_message?: string;
  created_at: string;
  updated_at: string;
  // Populated from joins
  source_files?: SourceFile[];
  processed_images?: ProcessedImage[];
}

export interface SourceFile {
  id: string;
  media_item_id: string;
  storage_path: string;
  original_filename?: string;
  exposure_value?: number;
  capture_time?: string;
  file_size_bytes?: number;
  width?: number;
  height?: number;
  iso?: number;
  aperture?: number;
  shutter_speed?: string;
  camera_model?: string;
  created_at: string;
  // Client-side only
  file?: File;
  preview_url?: string;
}

export interface ProcessedImage {
  id: string;
  media_item_id: string;
  url: string;
  storage_path: string;
  processing_type: ProcessingType;
  ai_modifications: Record<string, any>;
  width?: number;
  height?: number;
  file_size_bytes?: number;
  processing_time_ms?: number;
  created_at: string;
}

export interface UploadListingOptions {
  /** Stored on queue row as metadata.pipeline so the worker picks the backend. */
  hdrMergePipeline?: HdrMergePipeline;
}

export interface ProcessingQueueItem {
  id: string;
  media_item_id: string;
  job_type: ProcessingType;
  status: QueueStatus;
  priority: number;
  worker_id?: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  metadata: Record<string, any>;
  created_at: string;
}

// =========================================
// EXIF & STACKING TYPES
// =========================================

export interface ExifData {
  dateTimeOriginal?: Date;
  exposureTime?: number;
  fNumber?: number;
  iso?: number;
  exposureBiasValue?: number; // EV
  make?: string;
  model?: string;
  imageWidth?: number;
  imageHeight?: number;
}

export interface FileWithExif {
  file: File;
  exif: ExifData;
  previewUrl?: string;
}

export interface HDRStack {
  id: string;
  files: FileWithExif[];
  captureTimeStart: Date;
  folderName?: string;
}

export interface UploadGroup {
  id: string;
  name: string; // Listing name (address or folder name)
  stacks: HDRStack[];
  singleImages: FileWithExif[];
}

// =========================================
// UI STATE TYPES
// =========================================

export type ViewMode = 'before' | 'after' | 'compare';

export interface StudioToolState {
  activeTool: ProcessingType | null;
  isProcessing: boolean;
}

export interface UploadState {
  status: 'idle' | 'analyzing' | 'uploading' | 'complete' | 'error';
  progress: number;
  totalFiles: number;
  uploadedFiles: number;
  groups: UploadGroup[];
  error?: string;
}

// =========================================
// AUTH & USER TYPES
// =========================================

export interface UserProfile {
  id: string;
  email: string;
  credits: number;
  subscription_plan?: string;
  stripe_customer_id?: string;
  referral_code?: string;
}

export interface AuthState {
  user: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

// =========================================
// REFERRAL TYPES
// =========================================

export interface ReferralReward {
  id: string;
  bonus_credits: number;
  referrer_bonus_credits?: number;
  purchased_credits: number;
  created_at: string;
  referred_email: string;
  referred_name?: string;
  reward_kind?: string;
  status?: string;
}

export interface ReferralStats {
  success: boolean;
  referral_code: string;
  signup_count: number;
  friends_joined?: number;
  total_bonus_credits: number;
  credits_earned?: number;
  invite_base_url?: string;
  rewards: ReferralReward[];
}

// =========================================
// API RESPONSE TYPES
// =========================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface ProcessingResult {
  media_item_id: string;
  processed_image_url: string;
  processing_time_ms: number;
}
